import moment, { Moment } from 'moment';
import { HdPlanetKey } from '../../chart/bodygraph/planet-grid/hd-planet-display-config';
import {
  HdTransitPlanetSegments,
  HdTransitSegment,
  HdTransitStation,
  HdTransitStationDirection,
} from '../transit.models';
import { HdTransitRetrogradeSpan, isRetrogradeAt } from '../transit-retrograde.utils';
import { HD_SEASON_MARKERS, HdSeasonKey, hdQuarterStartBySunGate, hdSeasonMomentMs } from '../../chart/mandala/hd-season-markers';
import { hdRaveNewYearOf, isHdRaveNewYearIngress } from '../rave-new-year/hd-rave-new-year';

/**
 * Pure view-model-builders van de verticale transits-weergave (spec
 * `docs/superpowers/specs/2026-09-26-transits-verticale-tijdlijn-analyse.md`
 * §2.5): segmenten uit `TransitDataService` → per lokale kalenderdag de stand
 * bij de dagstart en de wissels van die dag.
 *
 * Tijdsconventie: de data is UTC (`transit.models`), de dagindeling is
 * **lokaal** (de kijker leeft in zijn eigen kalender). Daggrenzen lopen
 * altijd via moment's lokale dag-aritmetiek, zodat een zomertijd-overgang een
 * dag van 23 of 25 uur oplevert in plaats van een verschoven middernacht.
 */

const DAY_KEY_FORMAT = 'YYYY-MM-DD';

/** `poort.lijn`-stand; `line` is `null` bij poort-granulariteit (fallback van de API). */
export interface HdTransitGateLine {
  readonly gate: number;
  readonly line: number | null;
}

/**
 * Zonnewende of equinox: de zon op 0°, 90°, 180° of 270°, in lijn 2 van een
 * Vessel of Love-poort (25, 15, 46, 10), de aarde in de poort ertegenover.
 * Het tijdstip is geïnterpoleerd binnen de echte lijngrenzen van de zon
 * (`hd-season-markers`), nauwkeurig tot op de minuut.
 */
export interface HdTransitSeasonMoment {
  readonly key: HdSeasonKey;
  readonly atMs: number;
  readonly sunGate: number;
  readonly sunLine: number;
  readonly earthGate: number;
}

/** Kwartaalstart: de zon gaat een Sphinx-poort in (13 Initiatie, 2 Beschaving, 7 Dualiteit, 1 Mutatie). */
export interface HdTransitQuarterStart {
  /** Kwartaal-key uit `MANDALA_QUARTERS`. */
  readonly key: string;
  readonly gate: number;
  readonly atMs: number;
}

/** Stand van één planeet bij de start (00:00 lokaal) van een dag — de dagkop-regel. */
export interface HdTransitDayPosition extends HdTransitGateLine {
  readonly planetKey: HdPlanetKey;
  /** Richting om 00:00 zelf (exacte retrograde-perioden, niet de segment-vlag). */
  readonly retrograde: boolean;
}

/** Eén poort- of lijnwissel binnen een dag. */
export interface HdTransitDayShift {
  readonly type: 'shift';
  /** Stabiel per wissel: planeet + echte starttijd (`track` in de template). */
  readonly id: string;
  readonly planetKey: HdPlanetKey;
  readonly planetId: number;
  /** `gate`: de planeet gaat een andere poort in; `line`: lijnwissel binnen dezelfde poort. */
  readonly kind: 'gate' | 'line';
  readonly atMs: number;
  readonly atUtc: Moment;
  /** Einde van de nieuwe stand (= de volgende wissel van deze planeet). */
  readonly untilUtc: Moment;
  /** Hoelang de nieuwe stand actief blijft. */
  readonly durationMs: number;
  /**
   * Richting op het moment van de wissel. Keert de planeet later binnen deze
   * stand, dan staat dat station als eigen regel in de dag-flow.
   */
  readonly retrograde: boolean;
  readonly previous: HdTransitGateLine | null;
  readonly current: HdTransitGateLine;
  readonly next: HdTransitGateLine | null;
  /** Enkel bij de zon die een Sphinx-poort ingaat: het kwartaal dat hier opent. */
  readonly quarterStart?: HdTransitQuarterStart;
  /** Enkel bij de zon die poort 41 ingaat: het Rave-jaar dat hier begint (Rave New Year). */
  readonly raveNewYear?: number;
  /** Bronsegment met de échte tijden (detail-popup). */
  readonly segment: HdTransitSegment;
}

/** Station (keerpunt): de planeet draait retrograde of direct. */
export interface HdTransitDayStation {
  readonly type: 'station';
  readonly id: string;
  readonly planetKey: HdPlanetKey;
  readonly planetId: number;
  readonly atMs: number;
  readonly atUtc: Moment;
  readonly turnsTo: HdTransitStationDirection;
}

/** Seizoensmoment als eigen regel in de dag-flow (enkel wanneer de zon met lijnen geladen is). */
export interface HdTransitDaySeason {
  readonly type: 'season';
  readonly id: string;
  readonly atMs: number;
  readonly atUtc: Moment;
  readonly season: HdTransitSeasonMoment;
}

export type HdTransitDayEntry = HdTransitDayShift | HdTransitDayStation | HdTransitDaySeason;

/** Eén kalenderdag van de dag-flow. */
export interface HdTransitDay {
  /** Lokale dagsleutel `YYYY-MM-DD`. */
  readonly key: string;
  /** Lokale middernacht (epoch-ms). */
  readonly startMs: number;
  /** Volgende lokale middernacht (exclusief). */
  readonly endMs: number;
  readonly isToday: boolean;
  /** `true` zodra voor elke aangeleverde planeet de stand op 00:00 bekend is. */
  readonly complete: boolean;
  /** Stand bij de dagstart, in de aangeleverde planeetvolgorde. */
  readonly startPositions: readonly HdTransitDayPosition[];
  /** Wissels en stations van de dag, chronologisch. */
  readonly entries: readonly HdTransitDayEntry[];
}

/** Eén wissel in de maandkalender (tooltip-regel). */
export interface HdTransitCalendarShift extends HdTransitGateLine {
  readonly atMs: number;
  readonly kind: 'gate' | 'line';
}

/** De wissels van één planeet op één kalenderdag (één glyph in de dagcel). */
export interface HdTransitCalendarPlanetShifts {
  readonly planetKey: HdPlanetKey;
  /** `gate` zodra de planeet die dag minstens één keer van poort wisselt. */
  readonly kind: 'gate' | 'line';
  readonly shifts: readonly HdTransitCalendarShift[];
}

/** Eén lijn van een planeet binnen een kalenderdag, geknipt op de daggrenzen. */
export interface HdTransitLineDayPart extends HdTransitGateLine {
  readonly line: number;
  readonly fromMs: number;
  readonly toMs: number;
  /** De lijn die het grootste deel van de dag actief is (precies één per dag). */
  readonly dominant: boolean;
}

/** De lijn-dag van een planeet (in de UI: de zon) op één lokale kalenderdag. */
export interface HdTransitLineDay {
  /** Lokale dagsleutel `YYYY-MM-DD`. */
  readonly key: string;
  /** Chronologisch; meer dan één zodra de planeet die dag van lijn wisselt. */
  readonly parts: readonly HdTransitLineDayPart[];
  /** Zonnewende of equinox op deze dag (enkel voor de zon), anders afwezig. */
  readonly season?: HdTransitSeasonMoment;
  /** De zon gaat deze dag een Sphinx-poort in: een nieuw kwartaal van het wiel (enkel voor de zon). */
  readonly quarterStart?: HdTransitQuarterStart;
}

// ── Dagsleutels ───────────────────────────────────────────────────────────

/** Lokale dagsleutel (`YYYY-MM-DD`) van een tijdstip. */
export function hdTransitDayKey(timeMs: number): string {
  return moment(timeMs).format(DAY_KEY_FORMAT);
}

/** Lokale middernacht (epoch-ms) van een dagsleutel. */
export function hdTransitDayStartMs(dayKey: string): number {
  return moment(dayKey, DAY_KEY_FORMAT).valueOf();
}

/** Dagsleutel `days` kalenderdagen verder (negatief = terug). */
export function hdTransitAddDays(dayKey: string, days: number): string {
  return moment(dayKey, DAY_KEY_FORMAT).add(days, 'day').format(DAY_KEY_FORMAT);
}

/** Aantal kalenderdagen van `from` naar `to` (negatief als `to` vroeger valt). */
export function hdTransitDaysBetween(from: string, to: string): number {
  // Middag als ankerpunt: een zomertijd-overgang verschuift het verschil dan
  // hooguit een uur, nooit tot over een dag-afronding heen.
  const noon = (key: string) => moment(`${key} 12:00`, `${DAY_KEY_FORMAT} HH:mm`);
  return Math.round(noon(to).diff(noon(from), 'hours') / 24);
}

// ── Dag-flow ──────────────────────────────────────────────────────────────

function gateLineOf(segment: HdTransitSegment | undefined): HdTransitGateLine | null {
  return segment == null ? null : { gate: segment.gate, line: segment.line };
}

/**
 * Index van het segment dat `timeMs` bevat (`fromUtc <= t < toUtc`), of -1.
 * Segmenten zijn oplopend gesorteerd en sluiten aan (service-garantie).
 */
function segmentIndexAt(segments: readonly HdTransitSegment[], timeMs: number): number {
  let low = 0;
  let high = segments.length - 1;
  while (low <= high) {
    const middle = (low + high) >> 1;
    const segment = segments[middle];
    if (segment.toUtc.valueOf() <= timeMs) {
      low = middle + 1;
    } else if (segment.fromUtc.valueOf() > timeMs) {
      high = middle - 1;
    } else {
      return middle;
    }
  }
  return -1;
}

/** Index van het eerste segment dat op of na `timeMs` start, of `segments.length`. */
function firstSegmentStartingAtOrAfter(segments: readonly HdTransitSegment[], timeMs: number): number {
  let low = 0;
  let high = segments.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (segments[middle].fromUtc.valueOf() < timeMs) low = middle + 1;
    else high = middle;
  }
  return low;
}

/** Wissels van één planeet binnen `[startMs, endMs)`, met buurstanden. */
function planetShifts(
  planetKey: HdPlanetKey,
  planetId: number,
  segments: readonly HdTransitSegment[],
  retrogradeSpans: readonly HdTransitRetrogradeSpan[],
  startMs: number,
  endMs: number,
): HdTransitDayShift[] {
  const result: HdTransitDayShift[] = [];
  for (let index = firstSegmentStartingAtOrAfter(segments, startMs); index < segments.length; index++) {
    const segment = segments[index];
    const atMs = segment.fromUtc.valueOf();
    if (atMs >= endMs) break;
    const previous = segments[index - 1];
    result.push({
      type: 'shift',
      id: `${planetKey}:${atMs}`,
      planetKey,
      planetId,
      // Zonder vorige stand in de data (enkel aan de rand van het geladen
      // venster) is de wissel niet te duiden als lijnwissel: dan telt hij als
      // het binnenkomen van de poort.
      kind: previous == null || previous.gate !== segment.gate ? 'gate' : 'line',
      atMs,
      atUtc: segment.fromUtc,
      untilUtc: segment.toUtc,
      durationMs: segment.toUtc.valueOf() - atMs,
      // Niet `segment.retrograde`: die vlag hoort bij het midden van het segment.
      retrograde: isRetrogradeAt(retrogradeSpans, atMs),
      previous: gateLineOf(previous),
      current: { gate: segment.gate, line: segment.line },
      next: gateLineOf(segments[index + 1]),
      ...quarterStartOf(planetKey, segment, previous),
      ...(isHdRaveNewYearIngress(planetKey, segment, previous) ? { raveNewYear: hdRaveNewYearOf(atMs) } : {}),
      segment,
    });
  }
  return result;
}

/** Kwartaalstart voor een poortwissel van de zon naar een Sphinx-poort; `{}` in alle andere gevallen (spread-vriendelijk). */
function quarterStartOf(
  planetKey: HdPlanetKey,
  segment: HdTransitSegment,
  previous: HdTransitSegment | undefined,
): { quarterStart?: HdTransitQuarterStart } {
  if (planetKey !== 'sun' || (previous != null && previous.gate === segment.gate)) return {};
  const quarter = hdQuarterStartBySunGate(segment.gate);
  return quarter == null ? {} : { quarterStart: { key: quarter.key, gate: quarter.gate, atMs: segment.fromUtc.valueOf() } };
}

/**
 * Seizoensmomenten binnen `[startMs, endMs)` uit lijnsegmenten van de zon:
 * voor elk segment dat lijn 2 van een Vessel-poort is, het geïnterpoleerde
 * tijdstip van de cardinale lengte. Poort-granulariteit (`line == null`)
 * levert niets: dan is de lijn niet bekend.
 */
function seasonMomentsIn(segments: readonly HdTransitSegment[], startMs: number, endMs: number): HdTransitSeasonMoment[] {
  const result: HdTransitSeasonMoment[] = [];
  let index = segmentIndexAt(segments, startMs);
  if (index < 0) index = firstSegmentStartingAtOrAfter(segments, startMs);
  for (; index >= 0 && index < segments.length && segments[index].fromUtc.valueOf() < endMs; index++) {
    const segment = segments[index];
    if (segment.line == null) continue;
    for (const marker of HD_SEASON_MARKERS) {
      if (segment.gate !== marker.sunGate || segment.line !== marker.sunLine) continue;
      const atMs = Math.round(hdSeasonMomentMs(marker, segment.fromUtc.valueOf(), segment.toUtc.valueOf()));
      if (atMs >= startMs && atMs < endMs) {
        result.push({ key: marker.key, atMs, sunGate: marker.sunGate, sunLine: marker.sunLine, earthGate: marker.earthGate });
      }
    }
  }
  return result;
}

/** Kwartaalstart binnen `[startMs, endMs)`: de zon gaat een Sphinx-poort in (eerste segment van die poort). */
function quarterStartIn(segments: readonly HdTransitSegment[], startMs: number, endMs: number): HdTransitQuarterStart | undefined {
  for (let index = firstSegmentStartingAtOrAfter(segments, startMs); index >= 0 && index < segments.length; index++) {
    const segment = segments[index];
    if (segment.fromUtc.valueOf() >= endMs) break;
    const found = quarterStartOf('sun', segment, segments[index - 1]).quarterStart;
    if (found != null) return found;
  }
  return undefined;
}

function planetSeasons(planet: HdTransitPlanetSegments, startMs: number, endMs: number): HdTransitDaySeason[] {
  if (planet.planetKey !== 'sun') return [];
  return seasonMomentsIn(planet.segments, startMs, endMs).map(season => ({
    type: 'season' as const,
    id: `sun:season:${season.atMs}`,
    atMs: season.atMs,
    atUtc: moment.utc(season.atMs),
    season,
  }));
}

function planetStations(
  planetKey: HdPlanetKey,
  planetId: number,
  stations: readonly HdTransitStation[],
  startMs: number,
  endMs: number,
): HdTransitDayStation[] {
  return stations
    .filter(station => station.atUtc.valueOf() >= startMs && station.atUtc.valueOf() < endMs)
    .map(station => ({
      type: 'station' as const,
      id: `${planetKey}:station:${station.atUtc.valueOf()}`,
      planetKey,
      planetId,
      atMs: station.atUtc.valueOf(),
      atUtc: station.atUtc,
      turnsTo: station.turnsTo,
    }));
}

/**
 * Bouwt één dag van de dag-flow. `planets` bepaalt welke planeten meedoen én
 * hun volgorde (dagkop en gelijktijdige wissels); de caller filtert al op de
 * zichtbare legende-categorieën en de Chiron-setting.
 */
export function buildHdTransitDay(
  dayKey: string,
  planets: readonly HdTransitPlanetSegments[],
  todayKey: string,
): HdTransitDay {
  const startMs = hdTransitDayStartMs(dayKey);
  const endMs = moment(dayKey, DAY_KEY_FORMAT).add(1, 'day').valueOf();
  const startPositions: HdTransitDayPosition[] = [];
  const entries: HdTransitDayEntry[] = [];
  let complete = true;

  for (const planet of planets) {
    if (planet.planetKey == null) continue;
    const startIndex = segmentIndexAt(planet.segments, startMs);
    if (startIndex < 0) {
      complete = false;
    } else {
      const segment = planet.segments[startIndex];
      startPositions.push({
        planetKey: planet.planetKey,
        gate: segment.gate,
        line: segment.line,
        retrograde: isRetrogradeAt(planet.retrogradeSpans, startMs),
      });
    }
    entries.push(
      ...planetShifts(planet.planetKey, planet.planetId, planet.segments, planet.retrogradeSpans, startMs, endMs),
    );
    entries.push(...planetStations(planet.planetKey, planet.planetId, planet.stations, startMs, endMs));
    entries.push(...planetSeasons(planet, startMs, endMs));
  }

  // Stabiele sortering: gelijktijdige items behouden de planeetvolgorde.
  entries.sort((a, b) => a.atMs - b.atMs);

  return {
    key: dayKey,
    startMs,
    endMs,
    isToday: dayKey === todayKey,
    complete,
    startPositions,
    entries,
  };
}

// ── Maandkalender ─────────────────────────────────────────────────────────

/**
 * Wissels per lokale dag in `[fromKey, toKey]` (grenzen inbegrepen), per dag
 * één regel per wisselende planeet in de aangeleverde planeetvolgorde. Bij
 * `granularity = 'gate'` tellen enkel poortwissels (een lijnwissel binnen
 * dezelfde poort maakt de planeet die dag niet "wisselend").
 */
export function buildHdTransitCalendarDays(
  planets: readonly HdTransitPlanetSegments[],
  fromKey: string,
  toKey: string,
  granularity: 'gate' | 'line',
): ReadonlyMap<string, readonly HdTransitCalendarPlanetShifts[]> {
  const startMs = hdTransitDayStartMs(fromKey);
  const endMs = moment(toKey, DAY_KEY_FORMAT).add(1, 'day').valueOf();
  const byDay = new Map<string, HdTransitCalendarPlanetShifts[]>();

  for (const planet of planets) {
    if (planet.planetKey == null) continue;
    const perDay = new Map<string, HdTransitCalendarShift[]>();
    const allShifts = planetShifts(planet.planetKey, planet.planetId, planet.segments, planet.retrogradeSpans, startMs, endMs);
    for (const shift of allShifts) {
      if (granularity === 'gate' && shift.kind !== 'gate') continue;
      const dayKey = hdTransitDayKey(shift.atMs);
      const list = perDay.get(dayKey) ?? [];
      list.push({ atMs: shift.atMs, gate: shift.current.gate, line: shift.current.line, kind: shift.kind });
      perDay.set(dayKey, list);
    }
    for (const [dayKey, shifts] of perDay) {
      const dayList = byDay.get(dayKey) ?? [];
      dayList.push({
        planetKey: planet.planetKey,
        kind: shifts.some(shift => shift.kind === 'gate') ? 'gate' : 'line',
        shifts,
      });
      byDay.set(dayKey, dayList);
    }
  }

  return byDay;
}

// ── Lijn-dag (zon) ────────────────────────────────────────────────────────

/**
 * De lijn-dag per lokale dag in `[fromKey, toKey]` (grenzen inbegrepen): de
 * lijnen waarin de planeet die dag staat, chronologisch en geknipt op de
 * daggrenzen, met de lijn die het grootste deel van de dag actief is als
 * dominant (bij gelijke duur de eerste). Gemeten op de echte lengte van de
 * lokale dag, dus ook juist op een zomertijd-dag van 23 of 25 uur.
 *
 * Een zonlijn duurt ±22 à 24 uur: de zon wisselt bijna elke dag van lijn en
 * een paar keer per maand twee keer (drie lijnen op één dag). Een dag die de
 * segmenten niet volledig dekken (data nog onderweg, een gat, of poort-data
 * zonder lijn) ontbreekt in het resultaat: liever niets tonen dan een
 * verkeerde dominante lijn.
 */
export function buildHdTransitLineDays(
  planet: HdTransitPlanetSegments | null,
  fromKey: string,
  toKey: string,
): ReadonlyMap<string, HdTransitLineDay> {
  const days = new Map<string, HdTransitLineDay>();
  if (planet == null) return days;
  for (let dayKey = fromKey; dayKey <= toKey; dayKey = hdTransitAddDays(dayKey, 1)) {
    const day = lineDay(planet.segments, dayKey, planet.planetKey === 'sun');
    if (day != null) days.set(dayKey, day);
  }
  return days;
}

function lineDay(segments: readonly HdTransitSegment[], dayKey: string, trackSeasons: boolean): HdTransitLineDay | null {
  const startMs = hdTransitDayStartMs(dayKey);
  const endMs = moment(dayKey, DAY_KEY_FORMAT).add(1, 'day').valueOf();
  const spans: { gate: number; line: number; fromMs: number; toMs: number }[] = [];
  let coveredUntilMs = startMs;
  let index = segmentIndexAt(segments, startMs);
  while (index >= 0 && index < segments.length && coveredUntilMs < endMs) {
    const segment = segments[index++];
    if (segment.line == null || segment.fromUtc.valueOf() > coveredUntilMs) return null;
    const toMs = Math.min(segment.toUtc.valueOf(), endMs);
    const last = spans[spans.length - 1];
    if (last != null && last.gate === segment.gate && last.line === segment.line) {
      last.toMs = toMs;
    } else {
      spans.push({ gate: segment.gate, line: segment.line, fromMs: coveredUntilMs, toMs });
    }
    coveredUntilMs = toMs;
  }
  if (coveredUntilMs < endMs) return null;

  let dominantIndex = 0;
  spans.forEach((span, spanIndex) => {
    const longest = spans[dominantIndex];
    if (span.toMs - span.fromMs > longest.toMs - longest.fromMs) dominantIndex = spanIndex;
  });
  const season = trackSeasons ? seasonMomentsIn(segments, startMs, endMs)[0] : undefined;
  const quarterStart = trackSeasons ? quarterStartIn(segments, startMs, endMs) : undefined;
  return {
    key: dayKey,
    parts: spans.map((span, spanIndex) => ({ ...span, dominant: spanIndex === dominantIndex })),
    ...(season != null ? { season } : {}),
    ...(quarterStart != null ? { quarterStart } : {}),
  };
}
